import React, { useState } from 'react';
import { motion } from 'framer-motion';
import LeadsSelectAllDropdown from '../LeadsSelectAllDropdown/LeadsSelectAllDropdown';
import ConfirmationModal from '../../../../components/ConfirmationModal/ConfirmationModal';
import { SavedLead } from '../../../../services/leadsService';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Filter } from 'lucide-react';
import { exportToCsv } from '../../../../services/exportService'; // Novo import
import toast from 'react-hot-toast'; // IMPORT CORRIGIDO

interface Lead extends SavedLead { }

interface LeadsSearchActionsProps {
  onSearch: (query: string) => void;
  onFilterToggle: () => void; // Alterado para toggle
  onExport: (leadsToExport: Lead[]) => void; // Alterando a assinatura para receber os leads
  selectedLeads: string[]; // Alterado para string[]
  allLeads: Lead[];
  setSelectedLeads: (ids: string[]) => void; // Alterado para string[]
  onDeleteLeads: (ids: string[]) => void; // Alterado para string[]

  // Props para Envio em Massa
  onStartBulkSend: () => void;
  isSending: boolean;

  // Props para Verificação
  onVerifySelectedNumbers: () => Promise<void>;
  isVerifying: boolean;
  isWhatsappConnected: boolean;
}

const LeadsSearchActions: React.FC<LeadsSearchActionsProps> = ({
  onSearch,
  onFilterToggle,
  onExport,
  selectedLeads,
  allLeads,
  setSelectedLeads,
  onDeleteLeads,
  onStartBulkSend,
  isSending,
  onVerifySelectedNumbers,
  isVerifying,
  isWhatsappConnected,
}) => {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const handleSearch = () => {
    onSearch(searchQuery);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const selectedLeadsCount = selectedLeads.length;

  const handleConfirmDelete = () => {
    onDeleteLeads(selectedLeads);
    setIsDeleteModalOpen(false);
  };

  const isActionDisabled = selectedLeadsCount === 0 || isSending || isVerifying;

  // Lógica para o botão de verificação
  const hasUnverifiedSelected = allLeads.some(r =>
    selectedLeads.includes(r.id) && r.whatsapp_status !== 'Validado' && r.phone_number
  );

  const isVerifyDisabled = selectedLeadsCount === 0 || isVerifying || !hasUnverifiedSelected || !isWhatsappConnected;

  const verifyTooltip = !isWhatsappConnected
    ? t('Por favor, conecte sua instância do WhatsApp na aba Integrações.')
    : selectedLeadsCount === 0
      ? t('Selecione leads com números de telefone para verificar.')
      : !hasUnverifiedSelected
        ? t('Todos os leads selecionados já foram verificados ou não possuem telefone.')
        : t('Verificar se os números selecionados possuem WhatsApp ativo.');

  const handleExportSelected = () => {
    if (selectedLeadsCount === 0) {
      // O botão já está desabilitado, mas é bom ter uma checagem de fallback
      toast.error(t('Selecione pelo menos um resultado para exportar.'));
      return;
    }

    const leadsToExport = allLeads.filter(lead => selectedLeads.includes(lead.id));

    try {
      exportToCsv(leadsToExport, 'mi_buscador_leads_salvos_export.csv');
      // Não precisamos chamar onExport, pois a lógica está aqui
      // onExport(leadsToExport); 
      toast.success(t('Exportação iniciada! Verifique seus downloads.'));
    } catch (error) {
      toast.error(t('Falha ao exportar para CSV.'));
    }
  };


  return (
    <>
      <motion.div
        className="flex flex-col sm:flex-row gap-4 rounded-xl p-4 bg-white dark:bg-gray-800/30 border border-gray-200 dark:border-gray-700/50 card"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.5 }}
      >
        <div className="flex-grow">
          <motion.label
            className="flex flex-col min-w-40 h-12 w-full"
            whileFocus={{ scale: 1.02 }}
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
          >
            <div className="flex w-full flex-1 items-stretch rounded-lg h-full bg-gray-100 dark:bg-background-dark-elevated2 border border-transparent dark:border-primary-darkMode hover:bg-gray-200 dark:hover:bg-background-dark-elevated3 transition-colors">
              <div className="text-gray-500 dark:text-gray-400 flex items-center justify-center pl-4 rounded-l-lg border-r-0 bg-transparent">
                <span className="material-symbols-outlined">search</span>
              </div>
              <input
                className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-lg text-[#181411] dark:text-white focus:outline-0 focus:ring-0 border-none bg-transparent h-full placeholder:text-gray-500 dark:placeholder:text-gray-400 px-4 rounded-l-none border-l-0 pl-2 text-base font-normal leading-normal"
                placeholder={t('leads.search_placeholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={handleKeyPress}
              />
            </div>
          </motion.label>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          {/* Select All Dropdown */}
          <LeadsSelectAllDropdown
            allLeads={allLeads}
            selectedLeads={selectedLeads}
            setSelectedLeads={setSelectedLeads}
          />

          {/* Botão de Filtros */}
          <motion.button
            onClick={onFilterToggle}
            className="flex h-12 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-gray-100 dark:bg-background-dark-elevated2 px-4 hover:bg-gray-200 dark:hover:bg-background-dark-elevated3 transition-colors border border-transparent dark:border-primary-darkMode"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <Filter className="size-5 text-gray-600 dark:text-gray-400" />
            <p className="text-[#181411] dark:text-white text-sm font-medium leading-normal">{t('leads.action_filter')}</p>
          </motion.button>

          {/* Botão de Verificar WhatsApp */}
          <Link
            to={!isWhatsappConnected ? '/integracoes' : '#'}
            title={verifyTooltip}
            onClick={(e) => {
              if (!isWhatsappConnected) {
                e.preventDefault();
              } else if (isVerifyDisabled) {
                e.preventDefault();
              }
            }}
          >
            <motion.button
              onClick={onVerifySelectedNumbers}
              className="flex h-12 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-green-100 dark:bg-green-900/30 px-4 hover:bg-green-200 dark:hover:bg-green-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              whileHover={{ scale: isVerifyDisabled ? 1 : 1.05 }}
              whileTap={{ scale: isVerifyDisabled ? 1 : 0.95 }}
              disabled={isVerifyDisabled}
            >
              {isVerifying ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-lg text-green-600 dark:text-green-400">sync</span>
                  <p className="text-green-600 dark:text-green-400 text-sm font-medium leading-normal">{t('dashboard.verifying')}</p>
                </>
              ) : (
                <>
                  <span className="material-symbols-outlined text-lg text-green-600 dark:text-green-400">check_circle</span>
                  <p className="text-green-600 dark:text-green-400 text-sm font-medium leading-normal">{t('dashboard.action_verify')} ({selectedLeadsCount})</p>
                </>
              )}
            </motion.button>
          </Link>

          {/* Botão de Envio em Massa */}
          <motion.button
            onClick={onStartBulkSend}
            disabled={isActionDisabled}
            className="flex h-12 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-primary text-white px-4 hover:opacity-90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: isActionDisabled ? 1 : 1.05 }}
            whileTap={{ scale: isActionDisabled ? 1 : 0.95 }}
          >
            {isSending ? (
              <>
                <span className="material-symbols-outlined animate-spin">sync</span>
                {t('sidebar.bulk_sending')}
              </>
            ) : (
              <>
                <span className="material-symbols-outlined text-lg">send</span>
                <p className="text-sm font-bold leading-normal">{t('sidebar.bulk_send_button')} ({selectedLeadsCount})</p>
              </>
            )}
          </motion.button>

          {/* Botão de Excluir */}
          <motion.button
            onClick={() => setIsDeleteModalOpen(true)}
            className="flex h-12 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-red-100 dark:bg-red-900/30 px-4 hover:bg-red-200 dark:hover:bg-red-800/50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            whileHover={{ scale: isActionDisabled ? 1 : 1.05 }}
            whileTap={{ scale: isActionDisabled ? 1 : 0.95 }}
            disabled={isActionDisabled}
          >
            <span className="material-symbols-outlined text-lg text-red-600 dark:text-red-400">delete</span>
            <p className="text-red-600 dark:text-red-400 text-sm font-medium leading-normal">{t('dashboard.action_delete')} ({selectedLeadsCount})</p>
          </motion.button>

          <motion.button
            onClick={handleExportSelected}
            className="flex h-12 shrink-0 items-center justify-center gap-x-2 rounded-lg bg-gray-100 dark:bg-background-dark-elevated2 px-4 hover:bg-gray-200 dark:hover:bg-background-dark-elevated3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed border border-transparent dark:border-primary-darkMode"
            whileHover={{ scale: isActionDisabled ? 1 : 1.05 }}
            whileTap={{ scale: isActionDisabled ? 1 : 0.95 }}
            disabled={isActionDisabled}
          >
            <span className="material-symbols-outlined text-lg text-gray-600 dark:text-white">download</span>
            <p className="text-[#181411] dark:text-white text-sm font-medium leading-normal">{t('dashboard.action_export')} ({selectedLeadsCount})</p>
          </motion.button>
        </div>
      </motion.div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleConfirmDelete}
        title={t('modals.confirm_delete_title')}
        message={t('Você tem certeza que deseja excluir {{count}} lead(s) selecionado(s)? Esta ação não pode ser desfeita.', { count: selectedLeadsCount })}
        confirmText={t('Sim, Excluir Permanentemente')}
        cancelText={t('modals.cancel')}
      />
    </>
  );
};

export default LeadsSearchActions;